import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { getBrandId } from '../../helpers/getBrandId/getBrandId';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { QueryKeys, VENUE_SYNC_STATUS_QUERY } from '../QueryKeys';
import { EProductLibraryStatus } from '../../api/getProductLibrarySyncStatus/getProductLibrarySyncStatus';
import { getVenuesSyncStatus, IVenueSyncStatus } from '../../api/getVenuesSyncStatus/getVenuesSyncStatus';
import { useRef, useState } from 'react';
import { triggerVenueSync } from 'src/api/triggerVenueSync/triggerVenueSync';
import { useEvents } from 'src/hooks/useEvents/useEvents';
import { ENTITY_TYPES, EVENT_TYPES } from 'src/api/getEvents';

export interface IVenueLibraryStatus extends IVenueSyncStatus {
  callCount: number;
}

interface UseVenueLibraryStatusOptions {
  onSyncStatusUpdate?: (data: IVenueLibraryStatus) => void;
  queryOptions?: UseQueryOptions<IVenueLibraryStatus>;
  enabled?: boolean;
  initialEventId?: string;
}

// A sync whose event is still active can be resumed on page load.
const ACTIVE_STATUSES: string[] = [EProductLibraryStatus.PENDING, EProductLibraryStatus.PENDING_REVIEW];

export const useVenueLibraryStatus = ({
  onSyncStatusUpdate,
  queryOptions,
  enabled = true,
  initialEventId,
}: UseVenueLibraryStatusOptions = {}) => {
  const [callCount, setCallCount] = useState<number>(1);
  const [eventId, setEventId] = useState<string | undefined>(initialEventId);
  const eventIdRef = useRef(initialEventId);
  const updateEventId = (id: string | undefined) => {
    eventIdRef.current = id;
    setEventId(id);
  };
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const syncQueryKey = [getBrandId(), QueryKeys.venueSyncStatus];

  useEvents(
    { entity_type: ENTITY_TYPES.VENUE, event_type: EVENT_TYPES.IMPORT },
    {
      enabled,
      onSuccess: (data) => {
        const latestEvent = data?.data?.[0];
        if (latestEvent && ACTIVE_STATUSES.includes(latestEvent.status)) {
          updateEventId(eventIdRef.current ?? latestEvent.event_id);
        }
      },
    },
  );

  const mutation = useMutation({
    mutationFn: async () => {
      return triggerVenueSync();
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: syncQueryKey });

      queryClient.setQueryData(VENUE_SYNC_STATUS_QUERY, (old: IVenueLibraryStatus) => ({
        ...old,
        status: EProductLibraryStatus.PENDING,
      }));
      showSnackbar({
        status: SnackbarStatus.PROGRESS,
        message: 'Venue sync started',
      });
    },
    onSuccess: ({ event_id }) => {
      updateEventId(event_id);
    },
    onError: (error: unknown) => {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: error instanceof Error ? error.message : 'Failed to trigger sync',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: VENUE_SYNC_STATUS_QUERY });
    },
  });

  const query = useQuery({
    enabled: enabled && !!eventId,
    queryKey: VENUE_SYNC_STATUS_QUERY,
    queryFn: async () => {
      const status = await getVenuesSyncStatus(eventIdRef.current);
      return {
        callCount,
        ...status,
      };
    },
    onError: () => showSnackbar({ status: SnackbarStatus.ERROR, message: 'Unable to fetch venue sync status' }),
    refetchInterval: (data) => (data?.status === EProductLibraryStatus.PENDING ? 10 * 1000 : false),
    onSuccess: (data) => {
      setCallCount((prev) => prev + 1);
      if (onSyncStatusUpdate) onSyncStatusUpdate(data);
    },
    ...queryOptions,
  });

  return {
    isSyncInProgress: query.data?.status === EProductLibraryStatus.PENDING,
    triggerSync: mutation.mutate,
    syncInfo: query.data,
    eventId,
  };
};
